import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { usePropertyId } from '../../../../common/hooks/usePropertyId';
import { useRoomsData } from '../../../../common/hooks/useRoomsData';
import { OrdinaryButton } from '../../../../common/Buttons';
import CustomerDetailsForm from './CustomerDetailsForm';

/**
 * Step: customer details. Renders booking summary and CustomerDetailsForm.
 * Does not create a reservation; submits to advance to payment step.
 * Receives booking, onSubmit, onBack, onChange.
 */
const CustomerDetailsStep = ({ booking, onSubmit, onBack, onChange }) => {
  const propertyId = usePropertyId();
  const { queryResults: roomsQuery } = useRoomsData(propertyId);

  const { data: rooms = [] } = roomsQuery ?? {};
  const [formValid, setFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { checkin, checkout, adults, selectedRoom, selectedPlan, totalPrice, customer = {} } = booking;
  const room = rooms.find((r) => r.id === selectedRoom || String(r.id) === String(selectedRoom));

  const formatDate = (d) => {
    if (!d) return '—';
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleCustomerChange = useCallback(
    (field, value) => {
      onChange('customer', { ...customer, [field]: value });
    },
    [customer, onChange]
  );

  const canSubmit =
    formValid &&
    checkin &&
    checkout &&
    selectedRoom != null &&
    selectedPlan?.planId != null;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit();
    } catch (err) {
      setSubmitError(err?.message ?? 'Nie udało się przejść dalej. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }} disabled={submitting}>
        Wstecz
      </Button>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Podsumowanie rezerwacji
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Zameldowanie
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(checkin)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Wymeldowanie
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(checkout)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Pokój
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {room?.name ?? `Pokój nr ${selectedRoom ?? '—'}`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Liczba gości
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {adults ?? '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Cena całkowita
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {totalPrice != null ? `${totalPrice} PLN` : '—'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Dane kontaktowe
        </Typography>
        <CustomerDetailsForm
          customerInfo={customer}
          onCustomerChange={handleCustomerChange}
          onValidChange={setFormValid}
        />
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <OrdinaryButton
            text="Dalej"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerDetailsStep;
